import {
  EnquiryHandle,
  IServerSideGetRowsParams,
  combineFunctions,
  useServerEnquiry,
} from '@access-erp/common';
import { CustomDetailsProps, ErrorToast } from '@access-erp/ui-components';
import { Guid } from 'guid-typescript';
import { useEffect, useRef, useState } from 'react';

import { UserRole } from '@/config/roles.config';
import { CONTRACTS_PER_PAGE } from '@/config/common.config';
import { Forms } from '@/graphql/contracts/_generated_/contracts';
import { replaceAdvancedValueType } from '@/components/common/EnquiriesScreen/utils/advancedFilterHelpers';
import { EnvUtils } from '@/utils/env.utils';
import { useGetInitialFiltersSet } from '@/components/common/EnquiriesScreen/hooks/useGetInitialFiltersSet';
import { useRightPanel } from './hooks/useRightPanel';
import BulkUpdateContractModal from './components/BulkUpdateContractModal/BulkUpdateContractModal';
import {
  getBcLineTypeColumns,
  oppColumns,
  purchaseOrderColumns,
  shipToInvoiceToColumns,
  variableTypeColumns,
} from './columns/detailColumns';
import { UseTransactionTableColumnsReturn, useColumns } from './hooks/useColumns';
import useGetBCDetails from './hooks/useGetBCDetails';
import useGetContracts, { PaginatedFilteredContracts } from './hooks/useGetPaginatedFilteredContracts';
import { getSavedSearch } from '@/components/common/EnquiriesScreen/utils/savedSearch';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { getSubscriptionId } from '@/utils/auth/subscription.utils';
import { ContractsEnquiriesScreen } from '@/components/common/EnquiriesScreen/providers/ContractsEnquiriesScreen';

interface UseTableColumns extends UseTransactionTableColumnsReturn {
  goToContractsBySearch: (searchText: string) => void;
}

const ContractsPage = (): React.JSX.Element => {
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const gridRef = useRef<EnquiryHandle<{ id: string | number; }>>(null);
  const getBCDetailsData = useGetBCDetails();
  const { headerColumns, goToContractsBySearch } = useColumns() as UseTableColumns;
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();
  const [subscriptionId, setSubscriptionId] = useState<string | null | undefined>();
  const { getPaginatedFilteredContracts } = useGetContracts();
  const { getSavedFilterIfAny, savedFilters } = useGetInitialFiltersSet();
  const detailColumns =
    [
      ...getBcLineTypeColumns,
      ...variableTypeColumns,
      ...(!EnvUtils.isProduction() ? purchaseOrderColumns : []),
      ...(!EnvUtils.isProduction() ? shipToInvoiceToColumns : []),
      ...(!EnvUtils.isProduction() ? oppColumns : []),
    ];

  const enquiry = useServerEnquiry<{ id: string | number }, {}, { id: string | number }>({
    gridRef: gridRef as any,
    headerColumns: headerColumns as any,
    detailColumns: detailColumns as any,
    initialFilter: replaceAdvancedValueType(savedFilters),
  });

  const { tabsConfiguration, isVisibleBulkUpdateModal, closeBulkUpdateModal } = useRightPanel({
    selectedHeaderRows: enquiry.selectedHeaderRows,
    userRole,
  });

  const searchTextFromQuery = getSavedSearch?.()?.savedSearch;

  useEffect(() => {
    const getInitialData = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
      const subId = await getSubscriptionId();
      setSubscriptionId(subId);
    };

    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getData = async (
    params: IServerSideGetRowsParams,
    serverSideCriteria: any,
  ) => {
    await getSavedFilterIfAny(Forms.Contracts, params);

    const subId = subscriptionId ?? (await getSubscriptionId());
    const summary = enquiry.serverSide?.current?.search ?? searchTextFromQuery;

    const dto: PaginatedFilteredContracts = {
      after: serverSideCriteria.nextPageCursor,
      first: CONTRACTS_PER_PAGE,
      filter: {
        summary,
        ...(enquiry.serverSide?.current?.customFilters ?? {}),
        ...serverSideCriteria.groupingFilters,
      },
      sort: serverSideCriteria.userSetSorting,
      group: serverSideCriteria.groupByColumn as any,
    };

    const data = await getPaginatedFilteredContracts(dto);

    if (subId) {
      localStorage.setItem('currentContractsQuery:' + subId, JSON.stringify({ ...dto, group: null }));
    }

    setPageIsLoading(false);
    const searchIsCleared = enquiry.serverSide?.current?.search === '';
    if (enquiry.serverSide?.current?.search || searchIsCleared) {
      goToContractsBySearch(enquiry.serverSide?.current?.search!);
    }
    return {
      items:
        data?.items?.map((el) => ({
          ...el,
          productFamily: el?.productFamily?.length ? el.productFamily.join(',') : null,
          billingType: el?.billingType?.length ? el.billingType.join(',') : null,
          id: el?.id && !Guid.parse(el.id).isEmpty() ? el.id : Guid.create().toString(),
          hasDetailLines: el?.hasContractLines,
        })) ?? [],
      pageInfo: {
        hasNextPage: data?.pageInfo.hasNextPage ?? false,
        hasPreviousPage: data?.pageInfo.hasPreviousPage ?? false,
        endCursor: data?.pageInfo?.endCursor || undefined,
        startCursor: data?.pageInfo?.startCursor || undefined,
      },
    };
  };

  const customDetailsProps: CustomDetailsProps<{ id: string | number }> = {
    getData: getBCDetailsData,
    primaryHeaderKey: 'id',
    filtersRef: enquiry.filtersRef,
    detailRowSelection: 'none',
  } as unknown as CustomDetailsProps<{ id: string | number }>;

  enquiry.refreshSearch = combineFunctions(enquiry.refreshSearch);
  return (
    <>
      <ContractsEnquiriesScreen
        ref={gridRef}
        enquiry={enquiry}
        filterForm={Forms.ContractsBC}
        formName={Forms.ContractsBC}
        headerProps={{
          primaryTitle: 'Contracts',
          secondaryTitle: 'List',
          searchPlaceholder: 'Start typing Contract #, Contracted Name, ShipTo or InvoiceTo',
          showExportAll: userRole === UserRole.FULL_ACCESS || userRole === UserRole.ADMIN,
          showExportCurrent: false,
          search: enquiry.serverSide?.current?.search ?? searchTextFromQuery,
        }}
        detailProps={{
          exportFileName: 'Contracts',
          customDetailsProps,
          rowGroupPanelShow: 'always',
          masterDetail: true,
          rowSelection: 'none',
          getData,
          pageSize: CONTRACTS_PER_PAGE,
          suppressRowClickSelection: false,
          loading: pageIsLoading,
        }}
        rightPanelTabs={
          tabsConfiguration
        }
      />
      {isVisibleBulkUpdateModal && (
        <BulkUpdateContractModal onClose={closeBulkUpdateModal} userRole={userRole} />
      )}
      <ErrorToast />
    </>
  );
};

export default ContractsPage;
